import React, { useEffect, useState } from "react";
import {
    ApolloProvider,
    ApolloClient,
    InMemoryCache,
    HttpLink,
    ApolloLink,
    from,
  } from '@apollo/client';
  import { onError } from "@apollo/client/link/error";
  import { setContext } from '@apollo/link-context';
  import { useAuth0 } from '@auth0/auth0-react';
  import { useRollbar } from '@rollbar/react';
  import cubejs from '@cubejs-client/core';
  import { analytics } from './utils/analytics';

  export const CubeContext = React.createContext({cubejsApi: null})
  export const SuperAdminContext = React.createContext({impersonatedUser: null, setImpersonatedUser: ({companyId, userId}) => {}})

  const AuthorizedApolloProvider = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [impersonatedUser, setImpersonatedUser] = useState(null);
    const rollbar = useRollbar()

    // Initialize Mixpanel on mount
    useEffect(() => {
      const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;
      if (mixpanelToken) {
        try {
          analytics.init(mixpanelToken);
        } catch (error) {
          console.error('[Analytics] Error initializing Mixpanel:', error);
        }
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('[Analytics] REACT_APP_MIXPANEL_TOKEN not set');
      }
    }, []);

    const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
      // Requests sent without a token (anonymous / first visit) are expected to
      // be rejected by the API — don't report those to Rollbar
      const isAnonymousRequest = !operation.getContext().headers?.authorization;

      if (graphQLErrors) {
        graphQLErrors.forEach((error) => {
          if (isAnonymousRequest && error.message.includes('You need to be authorized')) {
            console.warn('[GraphQL] Unauthenticated request rejected:', operation.operationName);
            return;
          }
          rollbar.error(`[GraphQL error]: ${error.message}`,
            error,
            { operationName: operation.operationName, query: operation.query.loc.source.body, variables: operation.variables });

          // Track GraphQL errors in Mixpanel
          try {
            analytics.track('graphql_error', {
              errorMessage: error.message,
              operationName: operation.operationName,
              errorPath: error.path?.join('.'),
            });
          } catch (analyticsError) {
            console.error('[Analytics] Error tracking GraphQL error:', analyticsError);
          }
        });
      }
      if (networkError) {
        rollbar.error(`[Network error]: ${networkError.message}`, networkError);

        // Track network errors in Mixpanel
        try {
          analytics.track('network_error', {
            errorMessage: networkError.message,
            operationName: operation.operationName,
          });
        } catch (analyticsError) {
          console.error('[Analytics] Error tracking network error:', analyticsError);
        }
      }
    });
  
    const httpLink = new HttpLink({
      uri: process.env.REACT_APP_CONTIMO_API_URL,
    });
    const [cubejsApi, setCubejsAPI] = useState();

    const authLink = setContext(async (_, { headers, ...rest }) => {
      let token;
      try {
        token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_API_IDENTIFIER,
          });
          setCubejsAPI(cubejs( 
            token,
            {
              apiUrl: process.env.REACT_APP_CUBE_API_URL
            }
          ));
      } catch (error) {
        if (error.error === 'login_required') {
          // Expected for anonymous visitors — no Auth0 session yet
          console.warn('[Auth] No active Auth0 session, request will be sent without a token');
        } else if (error.error === 'consent_required') {
          // Auth0 never skips consent for localhost redirect URIs, so silent
          // re-auth fails here — run the app on a non-localhost host alias to fix
          console.warn('[Auth] Silent auth failed with consent_required, request will be sent without a token');
        } else {
          console.error('[Analytics] Error getting access token:', error);
        }
      }
  
      if (!token) {
        return { headers, ...rest };
      }
      
      const credentials = impersonatedUser ? {
        ...(impersonatedUser.userId ? {'user-id': impersonatedUser.userId.value} : {}),
        'company-id': impersonatedUser.companyId.value,
      } : {};

      return {
        ...rest,
        headers: {
          ...headers,
          ...credentials,
          authorization: `Bearer ${token}`,
        },
      };
    });
  
    const analyticsLink = new ApolloLink((operation, forward) => {
      const startTime = Date.now();
      return forward(operation).map((response) => {
        const operationName = operation.operationName;
        if (operationName) {
          const duration = Date.now() - startTime;
          const definition = operation.query.definitions.find(d => d.kind === 'OperationDefinition');
          const operationType = definition ? definition.operation : 'unknown';

          analytics.track('graphql_latency', {
            operation_name: operationName,
            operation_type: operationType,
            arguments: operation.variables,
            latency: duration,
          });
        }
        return response;
      });
    });
  
    const [client, setClient] = useState();
    
    const createApolloClient = () => new ApolloClient({
      link: from([errorLink, analyticsLink, authLink.concat(httpLink)]),
      cache: new InMemoryCache(),
    });

    if (!client) {
      setClient(createApolloClient());
    }
  
    useEffect(() => {
      setClient(createApolloClient());  
    }, [impersonatedUser])

    if (!client) {
      return null;
    }
  
    return (
      <CubeContext.Provider value={cubejsApi}>
        <SuperAdminContext.Provider value={{impersonatedUser, setImpersonatedUser}}>
          <ApolloProvider client={client}>
            {children}
          </ApolloProvider>
        </SuperAdminContext.Provider>
      </CubeContext.Provider>
    );
  };
  
  export default AuthorizedApolloProvider;
